import React from 'react';
import {
  FaInstagram, FaFacebook, FaLinkedin, FaYoutube, FaTwitter, FaXTwitter,
  FaThreads, FaTiktok, FaPinterest, FaSnapchat, FaRedditAlien, FaDiscord,
  FaWhatsapp, FaTelegram,
} from 'react-icons/fa6';
import { Share2 } from 'lucide-react';

/**
 * Central platform-icon registry.
 *
 * `PLATFORM_ICON_MAP` — the source of truth. Keyed by short icon-key strings
 * stored in the `sm_options.icon` field (e.g. "instagram", "tiktok", "generic"),
 * NOT by platform display names — so a rename in the admin UI never breaks
 * icon rendering, and platform keys stay stable across the API/DB boundary.
 *
 * Adding a new brand icon = one new entry here + one Font Awesome import.
 */
export const PLATFORM_ICON_MAP = {
  instagram:   { label: 'Instagram',    Node: FaInstagram,    color: 'text-pink-500' },
  facebook:    { label: 'Facebook',     Node: FaFacebook,     color: 'text-blue-600' },
  linkedin:    { label: 'LinkedIn',     Node: FaLinkedin,     color: 'text-blue-700' },
  youtube:     { label: 'YouTube',      Node: FaYoutube,      color: 'text-red-600' },
  twitter:     { label: 'Twitter',      Node: FaTwitter,      color: 'text-sky-500' },
  'x-twitter': { label: 'X (Twitter)',  Node: FaXTwitter,     color: 'text-slate-800' },
  threads:     { label: 'Threads',      Node: FaThreads,      color: 'text-slate-900' },
  tiktok:      { label: 'TikTok',       Node: FaTiktok,       color: 'text-slate-900' },
  pinterest:   { label: 'Pinterest',    Node: FaPinterest,    color: 'text-red-600' },
  snapchat:    { label: 'Snapchat',     Node: FaSnapchat,     color: 'text-yellow-500' },
  reddit:      { label: 'Reddit',       Node: FaRedditAlien,  color: 'text-orange-600' },
  discord:     { label: 'Discord',      Node: FaDiscord,      color: 'text-indigo-500' },
  whatsapp:    { label: 'WhatsApp',     Node: FaWhatsapp,     color: 'text-green-500' },
  telegram:    { label: 'Telegram',     Node: FaTelegram,     color: 'text-sky-500' },
  generic:     { label: 'Generic',      Node: Share2,         color: 'text-slate-500' },
};

/** Picker options for the admin form. Order shown in the UI dropdown. */
export const AVAILABLE_PLATFORM_ICONS = Object.entries(PLATFORM_ICON_MAP).map(
  ([key, v]) => ({ key, label: v.label }),
);

/**
 * Name → icon-key fallback. Used when a platform has no explicit icon in the
 * dynamic map (e.g. archived rows from before the icon field existed, or a
 * brand-new platform typed by the admin without picking an icon). If none of
 * these match either, `getPlatformIcon` falls back to the `generic` icon so
 * every platform ALWAYS renders something — never a blank.
 */
const NAME_TO_ICON_KEY = {
  'Instagram': 'instagram',
  'Facebook': 'facebook',
  'LinkedIn': 'linkedin',
  'YouTube': 'youtube',
  'Twitter': 'twitter',
  'X': 'x-twitter',
  'X (Twitter)': 'x-twitter',
  'Threads': 'threads',
  'TikTok': 'tiktok',
  'Pinterest': 'pinterest',
  'Snapchat': 'snapchat',
  'Reddit': 'reddit',
  'Discord': 'discord',
  'WhatsApp': 'whatsapp',
  'Telegram': 'telegram',
};

/** Render an icon directly by key (used by the admin picker preview). */
export const renderIconByKey = (iconKey, size = 16) => {
  const meta = PLATFORM_ICON_MAP[iconKey] || PLATFORM_ICON_MAP.generic;
  const { Node, color } = meta;
  return <Node size={size} className={color} />;
};

/** Get the icon-key string for a platform display name (used for both
 * rendering and platform<->link matching). Exported so PostsTable can reuse
 * the exact same resolution logic instead of a second hardcoded map. */
export const getPlatformIconKey = (platform, iconMap = null) => {
  if (iconMap && iconMap[platform] && PLATFORM_ICON_MAP[iconMap[platform]]) {
    return iconMap[platform];
  }
  return NAME_TO_ICON_KEY[platform] || 'generic';
};

/**
 * Get the JSX icon for a platform name.
 * @param {string} platform - display name (e.g. "Instagram", "TikTok")
 * @param {number} size - icon px size (default 16)
 * @param {Object|null} iconMap - optional {platformName: iconKey} map (from useSocialMediaOptions().PLATFORM_ICONS). Takes priority over NAME_TO_ICON_KEY.
 */
export const getPlatformIcon = (platform, size = 16, iconMap = null) => {
  return renderIconByKey(getPlatformIconKey(platform, iconMap), size);
};

export const PlatformIcon = ({ platform, size = 16, iconMap = null }) =>
  getPlatformIcon(platform, size, iconMap);

export const renderPlatformIcons = (platforms, size = 14, iconMap = null) => {
  if (!platforms || platforms.length === 0) return null;
  return platforms.map((p, idx) => (
    <span key={idx} className="inline-flex">{getPlatformIcon(p, size, iconMap)}</span>
  ));
};

/** Static fallback list — used only until the dynamic list arrives from the API. */
export const PLATFORMS = [
  'Instagram', 'Facebook', 'LinkedIn', 'YouTube',
  'X (Twitter)', 'Threads', 'TikTok', 'Pinterest',
];

// ─── Platform ⇄ post-link matching (Post Table platform-icon click) ─────────
// Coarser than icon keys on purpose: per product requirement, "Twitter" text
// and both x.com/twitter.com hostnames must resolve to the same bucket as the
// "X (Twitter)" platform, even though `twitter` and `x-twitter` render
// different icons above.
const MATCH_KEY_BY_ICON_KEY = { twitter: 'x', 'x-twitter': 'x' };
const toMatchKey = (iconKey) => MATCH_KEY_BY_ICON_KEY[iconKey] || iconKey;

const HOSTNAME_MATCH_RULES = [
  { host: 'instagram.com', key: 'instagram' },
  { host: 'instagr.am', key: 'instagram' },
  { host: 'facebook.com', key: 'facebook' },
  { host: 'fb.com', key: 'facebook' },
  { host: 'fb.watch', key: 'facebook' },
  { host: 'youtube.com', key: 'youtube' },
  { host: 'youtu.be', key: 'youtube' },
  { host: 'linkedin.com', key: 'linkedin' },
  { host: 'lnkd.in', key: 'linkedin' },
  { host: 'x.com', key: 'x' },
  { host: 'twitter.com', key: 'x' },
  { host: 'threads.net', key: 'threads' },
  { host: 'tiktok.com', key: 'tiktok' },
  { host: 'vm.tiktok.com', key: 'tiktok' },
  { host: 'pinterest.com', key: 'pinterest' },
  { host: 'pin.it', key: 'pinterest' },
  { host: 'snapchat.com', key: 'snapchat' },
  { host: 'reddit.com', key: 'reddit' },
  { host: 'discord.com', key: 'discord' },
  { host: 'discord.gg', key: 'discord' },
  { host: 'whatsapp.com', key: 'whatsapp' },
  { host: 'wa.me', key: 'whatsapp' },
  { host: 'telegram.org', key: 'telegram' },
  { host: 't.me', key: 'telegram' },
];

const matchKeyFromHostname = (url) => {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '').toLowerCase();
    const rule = HOSTNAME_MATCH_RULES.find((r) => host === r.host || host.endsWith(`.${r.host}`));
    return rule ? rule.key : null;
  } catch {
    return null;
  }
};

const matchKeyFromLabel = (label) => {
  if (!label) return null;
  const norm = label.trim().toLowerCase();
  const nameEntry = Object.entries(NAME_TO_ICON_KEY).find(([name]) => name.toLowerCase() === norm);
  if (nameEntry) return toMatchKey(nameEntry[1]);
  if (PLATFORM_ICON_MAP[norm]) return toMatchKey(norm);
  return null;
};

/**
 * Find the URL (from a post's `links` array) that belongs to `platform`.
 * Prefers each link's label metadata; falls back to hostname detection.
 * Returns null if no matching link exists — caller must NOT fall back to
 * another platform's link or generate a URL.
 */
export const findPlatformLink = (platform, links, iconMap = null) => {
  if (!links || !links.length) return null;
  const targetKey = toMatchKey(getPlatformIconKey(platform, iconMap));
  const match = links.find((l) => {
    if (!l?.url) return false;
    const key = matchKeyFromLabel(l.label) || matchKeyFromHostname(l.url);
    return key === targetKey;
  });
  return match ? match.url : null;
};

export default getPlatformIcon;
